/**
 * Permission Error Handler Utility
 * Handles permission-related errors (403 with sub-role restrictions)
 * across the entire application by listening for custom events
 * and displaying user-friendly notifications
 */

import notificationService from '../notificationService';

/**
 * Check if an error is a permission error
 * @param {Error} error - The error object from API call
 * @returns {boolean} True if this is a permission error
 */
export const isPermissionError = (error) => {
  if (!error?.response || error.response.status !== 403) {
    return false;
  }
  
  const errorData = error.response.data;
  return (
    errorData?.error === 'Insufficient sub-role permissions' ||
    (errorData?.category && errorData?.permission) ||
    errorData?.code === 'PERMISSION_DENIED' ||
    (errorData?.error && errorData?.error.toLowerCase().includes('permission'))
  );
};

/**
 * Extract permission error details from an error object
 * @param {Error} error - The error object from API call
 * @returns {Object|null} Permission error details or null
 */
export const extractPermissionErrorDetails = (error) => {
  if (!isPermissionError(error)) {
    return null;
  }
  
  const errorData = error.response.data;
  return {
    category: errorData.category || 'general',
    permission: errorData.permission || 'unknown',
    message: errorData.details || errorData.error || 'You do not have permission to perform this action',
    isPermissionError: true
  };
};

/**
 * Show a notification for permission errors
 * @param {Object} details - Permission error details
 * @param {string} details.message - Error message to display
 * @param {string} details.category - Category of the permission (e.g., 'funds')
 * @param {string} details.permission - Specific permission required
 */
export const showPermissionErrorNotification = (details) => {
  const { message, category, permission } = details;
  
  // Create a user-friendly message
  let notificationMessage = message;
  
  // If the message is generic, create a more specific one
  if (message === 'Insufficient sub-role permissions' || !message.includes('do not have')) {
    if (category === 'funds') {
      notificationMessage = 'You do not have permission to manage funds. Please contact your administrator.';
    } else if (category === 'groups') {
      notificationMessage = 'You do not have permission to manage groups. Please contact your administrator.';
    } else if (category === 'instruments') {
      notificationMessage = 'You do not have permission to manage instruments. Please contact your administrator.';
    } else if (category === 'users') {
      notificationMessage = 'You do not have permission to manage users. Please contact your administrator.';
    } else if (category === 'admins') {
      notificationMessage = 'You do not have permission to manage administrators. Please contact your administrator.';
    } else if (permission) {
      notificationMessage = `You do not have permission: ${permission}. Please contact your administrator.`;
    } else {
      notificationMessage = message;
    }
  }
  
  // Show the notification
  notificationService.error(notificationMessage);
};

/**
 * Handle permission errors from API calls
 * This function can be used in catch blocks to handle permission errors
 * @param {Error} error - The error object from API call
 * @returns {boolean} True if this was a permission error (handled), false otherwise
 */
export const handlePermissionError = (error) => {
  const details = extractPermissionErrorDetails(error);
  
  if (details) {
    console.error('[PermissionErrorHandler] Permission denied:', details);
    // Don't show notification here - the global listener already handled it
    // The global listener is set up in App.js via setupPermissionErrorListener
    // This function just returns true to indicate it was a permission error
    return true;
  }
  
  return false;
};

/**
 * Set up a global listener for permission-denied events
 * Call this once in your app initialization (e.g., in App.js or main Dashboard component)
 * @returns {Function} Cleanup function to remove the event listener
 */
export const setupPermissionErrorListener = () => {
  const handlePermissionDenied = (event) => {
    const { category, permission, message, rawData } = event.detail;
    console.error('[PermissionErrorHandler] Global permission denied event:', {
      category,
      permission,
      message,
      rawData
    });
    showPermissionErrorNotification({ category, permission, message });
  };
  
  window.addEventListener('permission-denied', handlePermissionDenied);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('permission-denied', handlePermissionDenied);
  };
};

// Export a default instance for convenience
export default {
  isPermissionError,
  extractPermissionErrorDetails,
  showPermissionErrorNotification,
  handlePermissionError,
  setupPermissionErrorListener
};
